import React, { useRef } from "react";
import { Eraser, MousePointer2, Pencil, Plus, Smile } from "lucide-react";
import { BOARD_TOOLS, DRAW_COLORS } from "../../lib/boardConstants";
import { cn } from "../../lib/utils";

const TOOL_BUTTONS = [
  { id: BOARD_TOOLS.select, label: "Select", icon: MousePointer2 },
  { id: BOARD_TOOLS.draw, label: "Draw", icon: Pencil },
  { id: BOARD_TOOLS.emoji, label: "Emoji", icon: Smile },
  { id: BOARD_TOOLS.eraser, label: "Eraser", icon: Eraser },
];

const iconBtn =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--t-event-pill-radius)] text-[var(--t-meta)] transition-colors hover:bg-black/[0.04] hover:text-[var(--t-text)]";

function ToolSlidePanel({ open, children }) {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center overflow-hidden transition-[max-width,opacity] duration-200 ease-out",
        open ? "max-w-[min(100vw,520px)] opacity-100" : "max-w-0 opacity-0",
      )}
      aria-hidden={!open}
    >
      <div
        className={cn(
          "flex items-center gap-1 pl-1 transition-transform duration-200 ease-out",
          open ? "translate-x-0" : "-translate-x-2",
        )}
      >
        {children}
      </div>
    </div>
  );
}

function EmojiPickerRow({ emojis, selectedEmoji, onSelectEmoji, onAddCustomEmoji }) {
  const inputRef = useRef(null);

  return (
    <div className="flex items-center gap-0.5 overflow-x-auto [-webkit-overflow-scrolling:touch]">
      {emojis.map((emoji) => (
        <button
          key={emoji}
          type="button"
          className={cn(
            "shrink-0 rounded-[var(--t-event-pill-radius)] px-1.5 py-1 text-lg transition-colors hover:bg-black/[0.04]",
            selectedEmoji === emoji && "bg-[var(--t-accent)]/10",
          )}
          onClick={() => onSelectEmoji(emoji)}
        >
          <span className="select-none">{emoji}</span>
        </button>
      ))}
      <span className="relative inline-flex shrink-0">
        <button
          type="button"
          aria-label="Add custom emoji"
          title="Add emoji"
          className={cn(iconBtn, "h-7 w-7")}
          onClick={() => inputRef.current?.focus()}
          data-testid="board-add-custom-emoji"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
        <input
          ref={inputRef}
          type="text"
          inputMode="text"
          aria-label="Custom emoji"
          className="pointer-events-none absolute left-0 top-0 h-px w-px opacity-0"
          onChange={(e) => {
            const raw = e.target.value.trim();
            if (!raw) return;
            const emoji = [...raw][0];
            if (!emoji) return;
            onAddCustomEmoji?.(emoji);
            onSelectEmoji(emoji);
            e.target.value = "";
          }}
        />
      </span>
    </div>
  );
}

export function BoardToolbar({
  onAddSticky,
  connected,
  tool,
  onToolChange,
  drawColor,
  onDrawColorChange,
  drawWidth,
  onDrawWidthChange,
  selectedEmoji,
  onSelectEmoji,
  emojiOptions = [],
  onAddCustomEmoji,
}) {
  return (
    <div className="mb-2 flex min-w-0 items-center gap-1 overflow-x-auto flex-nowrap pb-0.5 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <button
        type="button"
        onClick={onAddSticky}
        aria-label="Add sticky"
        title="Add sticky"
        className={iconBtn}
        data-testid="board-add-sticky"
      >
        <Plus className="h-4 w-4" />
      </button>

      <div className="mx-0.5 h-4 w-px shrink-0 bg-[var(--t-border)]" aria-hidden />

      <div className="flex shrink-0 items-center gap-0.5">
        {TOOL_BUTTONS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            aria-label={label}
            title={label}
            onClick={() => onToolChange(id)}
            className={cn(
              iconBtn,
              tool === id && "bg-[var(--t-accent)] text-white hover:bg-[var(--t-accent)] hover:text-white",
            )}
            data-testid={`board-tool-${id}`}
          >
            <Icon className="h-4 w-4" />
          </button>
        ))}
      </div>

      <ToolSlidePanel open={tool === BOARD_TOOLS.draw}>
        <div className="flex items-center gap-1.5 overflow-x-auto overflow-y-visible py-0.5 [-webkit-overflow-scrolling:touch]">
          {DRAW_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              aria-label={`Draw color ${color}`}
              aria-pressed={drawColor === color}
              className={cn(
                "h-4 w-4 shrink-0 rounded-full border-2 transition-colors",
                drawColor === color
                  ? "border-[var(--t-accent)]"
                  : "border-black/10 hover:border-black/20",
              )}
              style={{ backgroundColor: color }}
              onClick={() => onDrawColorChange(color)}
            />
          ))}
          {[2, 4, 8].map((width) => (
            <button
              key={width}
              type="button"
              onClick={() => onDrawWidthChange(width)}
              className={cn(
                "shrink-0 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide text-[var(--t-meta)] transition-colors hover:text-[var(--t-text)]",
                drawWidth === width && "bg-[var(--t-accent)]/10 text-[var(--t-accent)]",
              )}
            >
              {width === 2 ? "Thin" : width === 4 ? "Med" : "Thick"}
            </button>
          ))}
        </div>
      </ToolSlidePanel>

      <ToolSlidePanel open={tool === BOARD_TOOLS.emoji}>
        <EmojiPickerRow
          emojis={emojiOptions}
          selectedEmoji={selectedEmoji}
          onSelectEmoji={onSelectEmoji}
          onAddCustomEmoji={onAddCustomEmoji}
        />
      </ToolSlidePanel>

      <span
        className="ml-auto inline-flex shrink-0 items-center px-1"
        data-testid="board-connection-status"
        title={connected ? "Live" : "Connecting…"}
      >
        <span
          className={cn(
            "h-1.5 w-1.5 rounded-full",
            connected ? "bg-green-500" : "animate-pulse bg-amber-500",
          )}
        />
      </span>
    </div>
  );
}
